/**
 * @fileoverview Task management link validation utilities.
 * Provides recursive link validation for complex action chains including
 * capture, set, goto, startflow, callApi, and handover events.
 * @module helperMethods/task_management/util
 */

import { doesEventExist, doesLinkExist, doesApiMappingExist } from "../editor";
import { CaptureData, GotoData, ApiMappingData } from "./type";
import store from "@/store";
import _ from "lodash";

/**
 * Recursively validates that a link (string or action object) points to valid targets.
 * Handles complex action chains like capture->goto, set->callApi, etc.
 *
 * @param {string | CaptureData | GotoData | ApiMappingData} next - Link to validate
 * @returns {boolean} True if the link is valid and all nested links are valid
 *
 * @example
 * isLinkFound('content_node_123');
 * // Returns: true if content_node_123 exists
 *
 * @example
 * isLinkFound({
 *   event: 'capture',
 *   data: {
 *     key: 'name',
 *     value: '{{user_input}}',
 *     next: { event: 'goto', data: 'next_node' }
 *   }
 * });
 * // Returns: true if 'next_node' exists
 */
export function isLinkFound(next: string | CaptureData | GotoData | ApiMappingData): boolean {
  if (typeof next === "string") {
    const linkNodeFound = doesLinkExist(next);
    return linkNodeFound;
  }

  const nextEvent = _.get(next, "event");
  const nextData = _.get(next, "data");
  const nextIsEvent = doesEventExist(nextEvent);
  if (!nextIsEvent) {
    return false;
  }

  if (nextEvent === "handover_router" || nextEvent === "handover_livechat_start") {
    return true;
  }

  if (nextEvent === "callApi") {
    const mappingExists =
      typeof nextData === "object" && "mappingName" in nextData
        ? doesApiMappingExist(nextData.mappingName)
        : false;
    return mappingExists;
  }

  let finalNextEventToValidate = nextEvent;
  let finalNextDataToValidate = nextData as string;

  if (nextEvent === "capture" || nextEvent === "set") {
    const captureEventNext = _.get(next, "data.next.event");
    const captureDataNext = _.get(next, "data.next");

    finalNextEventToValidate = captureEventNext as string;
    finalNextDataToValidate = captureDataNext;
  }

  const isValidEvent = isLinkFound(finalNextEventToValidate);
  let isValidData = isLinkFound(finalNextDataToValidate);

  if (nextEvent === "startflow") {
    const flowId = _.get(next, "data.id");
    const flowIds = Object.keys(store.state.floweditor.contents).join("");
    isValidData = !flowIds.length ? true : flowIds.includes(flowId);
  }

  const isValidLink = isValidEvent && isValidData;
  return isValidLink;
}
